import React, { useState } from "react";
import { Upload, FileText, CheckCircle2, AlertCircle, X, Database, Eye, EyeOff, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { parseCSV, validateLeadRow, csvRowToLead, ParsedCSVRow } from "../utils/csvParser";
import { crmService } from "../crmApi";

interface ValidationResult {
  isValid: boolean;
  totalRows: number;
  invalidRows: number;
  headers: string[];
  errors: Array<{ row: number; field: string; message: string }>;
  preview: Array<{ data: ParsedCSVRow; errors: string[] }>;
}

const CRMBulkUpload = () => {
    const [file, setFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState<"idle" | "success" | "error" | "validating">("idle");
    const [errorMessage, setErrorMessage] = useState("");
    const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
    const [showPreview, setShowPreview] = useState(false);
    const { toast } = useToast();

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            if (selectedFile.type !== "text/csv" && !selectedFile.name.endsWith(".csv")) {
                toast({
                    title: "Invalid file type",
                    description: "Please upload a CSV file.",
                    variant: "destructive",
                });
                return;
            }
            setFile(selectedFile);
            setUploadStatus("idle");
            setErrorMessage("");
            setValidationResult(null);
            validateCSVFile(selectedFile);
        }
    };

    const validateCSVFile = async (csvFile: File) => {
        setUploadStatus("validating");
        try {
            const csvContent = await csvFile.text();
            const { headers, rows, errors: parseErrors } = parseCSV(csvContent);

            if (rows.length === 0) {
                setUploadStatus("error");
                setErrorMessage("CSV file contains no data rows");
                return;
            }

            const validationErrors: Array<{ row: number; field: string; message: string }> = [...parseErrors];
            const preview: Array<{ data: ParsedCSVRow; errors: string[] }> = [];
            let invalidCount = 0;

            rows.forEach((row, index) => {
                const rowErrors = validateLeadRow(row, headers);
                const errorMessages = rowErrors.map(e => e.message);
                
                if (errorMessages.length > 0) {
                    invalidCount++;
                    validationErrors.push(...rowErrors.map(e => ({ row: index + 2, field: e.field, message: e.message })));
                }
                
                preview.push({ data: row, errors: errorMessages });
            });

            setValidationResult({
                isValid: invalidCount === 0,
                totalRows: rows.length,
                invalidRows: invalidCount,
                headers,
                errors: validationErrors,
                preview: preview.slice(0, 5) // Show first 5 rows in preview
            });
            
            setUploadStatus(invalidCount === 0 ? "idle" : "error");
            
            if (invalidCount > 0) {
                toast({
                    title: "Validation Issues Found",
                    description: `${invalidCount} of ${rows.length} rows have validation errors.`,
                    variant: "destructive",
                });
            } else {
                toast({
                    title: "CSV Valid",
                    description: `All ${rows.length} rows passed validation.`,
                });
            }
        } catch (error: any) {
            setUploadStatus("error");
            setErrorMessage("Failed to parse CSV file: " + error.message);
        }
    };

    const handleUpload = async () => {
        if (!file || !validationResult?.isValid) return;

        setIsUploading(true);
        setUploadStatus("idle");
        
        try {
            const csvContent = await file.text();
            const { headers, rows } = parseCSV(csvContent);
            
            const leads = rows.map(row => csvRowToLead(row));
            
            const response = await crmService.bulkImportLeads({
                leads,
                total: leads.length
            });

            setUploadStatus("success");
            setFile(null);
            setValidationResult(null);
            
            toast({
                title: "Upload Successful",
                description: `Successfully imported ${response.data.created || 0} leads.`,
            });
        } catch (error: any) {
            setUploadStatus("error");
            const msg = error.response?.data?.error?.detail || error.response?.data?.error || "Failed to upload file. Please check the format.";
            setErrorMessage(msg);
            toast({
                title: "Upload Failed",
                description: msg,
                variant: "destructive",
            });
        } finally {
            setIsUploading(false);
        }
    };

    const removeFile = () => {
        setFile(null);
        setUploadStatus("idle");
        setErrorMessage("");
        setValidationResult(null);
    };

    const handleDownloadTemplate = async () => {
        try {
            const response = await crmService.downloadSampleCSV();
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'crm_import_template.csv');
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast({
                title: "Download failed",
                description: "Could not download the sample CSV template",
                variant: "destructive",
            });
        }
    };

    return (
        <div className="max-w-6xl mx-auto w-full">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center gap-3">
                    <Database className="h-5 w-5 text-intellend-green" />
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Bulk Upload Leads</h2>
                        <p className="text-sm text-gray-500">Upload a CSV file to import multiple leads at once</p>
                    </div>
                </div>

                <div className="p-8">
                    {!file ? (
                        <div 
                            className="border-2 border-dashed border-gray-300 rounded-xl p-12 flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                            onClick={() => document.getElementById("csv-upload")?.click()}
                        >
                            <div className="bg-white p-4 rounded-full shadow-sm mb-4">
                                <Upload className="h-8 w-8 text-intellend-green" />
                            </div>
                            <p className="text-lg font-medium text-gray-700">Click to upload or drag and drop</p>
                            <p className="text-sm text-gray-500 mt-1">CSV files only (max. 10MB)</p>
                            <input 
                                id="csv-upload"
                                type="file" 
                                accept=".csv" 
                                className="hidden" 
                                onChange={handleFileChange}
                            />
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center gap-3">
                                    <div className="bg-white p-2 rounded-md shadow-sm">
                                        <FileText className="h-6 w-6 text-intellend-green" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900">{file.name}</p>
                                        <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={removeFile}
                                    className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                                    disabled={isUploading}
                                >
                                    <X className="h-5 w-5 text-gray-500" />
                                </button>
                            </div>

                            {uploadStatus === "validating" && (
                                <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-800">
                                    <div className="animate-spin">
                                        <AlertCircle className="h-5 w-5 mt-0.5" />
                                    </div>
                                    <div>
                                        <p className="font-bold">Validating CSV...</p>
                                        <p className="text-sm">Checking file format and data</p>
                                    </div>
                                </div>
                            )}

                            {uploadStatus === "success" && (
                                <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
                                    <CheckCircle2 className="h-5 w-5 mt-0.5" />
                                    <div>
                                        <p className="font-bold">Upload successful!</p>
                                        <p className="text-sm">The data has been imported and added to the system.</p>
                                    </div>
                                </div>
                            )}

                            {uploadStatus === "error" && validationResult && (
                                <div className="space-y-4">
                                    <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                                        <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                                        <div className="flex-grow">
                                            <p className="font-bold">Validation Failed</p>
                                            <p className="text-sm">{validationResult.invalidRows} of {validationResult.totalRows} rows have errors.</p>
                                        </div>
                                    </div>

                                    {validationResult.errors.length > 0 && (
                                        <div className="border border-red-200 rounded-lg bg-red-50 overflow-hidden">
                                            <button
                                                onClick={() => setShowPreview(!showPreview)}
                                                className="w-full px-4 py-3 text-left font-medium text-red-800 hover:bg-red-100 flex items-center justify-between"
                                            >
                                                <span className="flex items-center gap-2">
                                                    {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                    View Error Details ({validationResult.errors.length})
                                                </span>
                                            </button>
                                            {showPreview && (
                                                <div className="px-4 py-3 border-t border-red-200 max-h-64 overflow-y-auto space-y-2 bg-white">
                                                    {validationResult.errors.slice(0, 20).map((error, idx) => (
                                                        <div key={idx} className="text-sm">
                                                            <span className="text-red-600 font-medium">Row {error.row}, Field "{error.field}":</span>
                                                            <span className="text-red-700 ml-2">{error.message}</span>
                                                        </div>
                                                    ))}
                                                    {validationResult.errors.length > 20 && (
                                                        <div className="text-sm text-red-600">... and {validationResult.errors.length - 20} more errors</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {validationResult && validationResult.isValid && (
                                <div className="border border-green-200 rounded-lg bg-green-50 p-4">
                                    <div className="flex items-start gap-3">
                                        <CheckCircle2 className="h-5 w-5 mt-0.5 text-green-700" />
                                        <div className="flex-grow">
                                            <p className="font-bold text-green-800">CSV validation successful</p>
                                            <p className="text-sm text-green-700">All {validationResult.totalRows} rows are valid and ready to import.</p>
                                        </div>
                                    </div>
                                    {validationResult.preview.length > 0 && (
                                        <div className="mt-4">
                                            <button
                                                onClick={() => setShowPreview(!showPreview)}
                                                className="text-sm font-medium text-green-700 hover:text-green-800 flex items-center gap-2"
                                            >
                                                {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                {showPreview ? 'Hide' : 'Show'} Preview
                                            </button>
                                            {showPreview && (
                                                <div className="mt-3 max-h-64 overflow-x-auto">
                                                    <table className="text-xs">
                                                        <thead>
                                                            <tr className="bg-green-100">
                                                                {validationResult.headers.slice(0, 8).map((header) => (
                                                                    <th key={header} className="px-2 py-1 text-left font-medium text-green-800">{header}</th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {validationResult.preview.map((row, idx) => (
                                                                <tr key={idx} className="border-t border-green-200 hover:bg-green-100">
                                                                    {validationResult.headers.slice(0, 8).map((header) => (
                                                                        <td key={header} className="px-2 py-1 text-green-700 truncate max-w-[150px]">
                                                                            {row.data[header] || '-'}
                                                                        </td>
                                                                    ))}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="flex justify-end gap-3">
                                <Button 
                                    variant="outline" 
                                    onClick={removeFile}
                                    disabled={isUploading}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    className="bg-intellend-green hover:bg-intellend-green/90 text-white"
                                    onClick={handleUpload}
                                    disabled={isUploading || !validationResult?.isValid}
                                >
                                    {isUploading ? "Uploading..." : "Import Leads"}
                                </Button>
                            </div>
                        </div>
                    )}

                    <div className="mt-8 border-t border-gray-100 pt-6">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 text-amber-500" />
                            CSV Format Requirements
                        </h3>
                        <div className="text-sm text-gray-600 space-y-4">
                            <div>
                                <p className="font-medium text-gray-900 mb-2">Required columns:</p>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li><code className="bg-gray-100 px-2 py-0.5 rounded">trade_name</code> - Business trade name</li>
                                    <li><code className="bg-gray-100 px-2 py-0.5 rounded">contact_name</code> - Contact person name</li>
                                    <li><code className="bg-gray-100 px-2 py-0.5 rounded">contact_phone</code> - Phone number (7-15 digits)</li>
                                    <li><code className="bg-gray-100 px-2 py-0.5 rounded">status</code> - Lead status value</li>
                                    <li><code className="bg-gray-100 px-2 py-0.5 rounded">role</code> - Contact role</li>
                                </ul>
                            </div>
                            <div>
                                <p className="font-medium text-gray-900 mb-2">Optional columns:</p>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li><code className="bg-gray-100 px-2 py-0.5 rounded">contact_email</code> - Email address</li>
                                    <li><code className="bg-gray-100 px-2 py-0.5 rounded">primary_address_*</code> - Primary address fields</li>
                                    <li><code className="bg-gray-100 px-2 py-0.5 rounded">contact_phone_type</code> - 'mobile' or 'landline' for the primary contact</li>
                                    <li><code className="bg-gray-100 px-2 py-0.5 rounded">additional_contacts</code> - Multiple contacts; use Type for contact role (secondary/other) and Phone Type for mobile/landline</li>
                                    <li><code className="bg-gray-100 px-2 py-0.5 rounded">additional_addresses</code> - Multiple addresses</li>
                                    <li>And other CRM fields as needed</li>
                                </ul>
                            </div>
                            <div>
                                <p className="font-medium text-gray-900 mb-2">Multiple Addresses & Phone Numbers:</p>
                                <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-2 mt-2">
                                    <p className="text-xs font-medium text-blue-900">Use pipe (|) to separate multiple entries:</p>
                                    <div>
                                        <code className="bg-white px-2 py-1 rounded text-xs block mb-1 break-words">
                                          <span className="text-blue-700">additional_contacts:</span> "Name1:Phone1:secondary:mobile|Name2:Phone2:other:landline"
                                        </code>
                                        <span className="text-xs text-blue-800">Example: "Jane Smith:9876543211:secondary:mobile|Bob Johnson:9876543212:other:landline"</span>
                                    </div>
                                    <div>
                                        <code className="bg-white px-2 py-1 rounded text-xs block mb-1 break-words">
                                          <span className="text-blue-700">additional_addresses:</span> "Type:Line1:Line2:Line3:Pincode:Landmark|..."
                                        </code>
                                        <span className="text-xs text-blue-800">Example: "secondary:456 Tech Park:Building A:New Delhi:110002:Tech Campus|warehouse:789 Industrial Zone::Gurgaon:122001:"</span>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <p className="font-medium text-gray-900 mb-2">Tips:</p>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Download a sample CSV from the Lead Table using "CRM Data CSV" button</li>
                                    <li>Or download our template below to see examples with multiple addresses and contacts</li>
                                    <li>Fields with commas, quotes, or newlines should be wrapped in double quotes</li>
                                    <li>Empty cells are acceptable for optional fields</li>
                                    <li>Maximum 1000 rows per upload for optimal performance</li>
                                </ul>
                            </div>

                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleDownloadTemplate}
                                    className="flex items-center gap-2"
                                >
                                    <Download className="h-4 w-4" /> Download CSV Template
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CRMBulkUpload;
